import { useState } from 'react';
import { PackageCheck, Search, Printer } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useGoodsReceiptActionMutation, useGoodsReceiptQuery, useGoodsReceiptsQuery,
  type GoodsReceipt,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, PageHeader,
  Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { PurchaseSlip } from '@/components/PurchaseSlip';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'warning'> = {
  DRAFT: 'warning',
  POSTED: 'success',
  CANCELLED: 'neutral',
};

/**
 * Deliveries that have arrived. A receipt sits as a draft until it is posted,
 * so what came in at the gate can be checked before it counts as stock.
 */
export function GoodsReceiptsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [slipFor, setSlipFor] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useGoodsReceiptsQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    search: search || undefined,
    status: status || undefined,
  });
  const slipReceipt = useGoodsReceiptQuery(slipFor ?? '', { skip: !slipFor });
  const [runAction, actionState] = useGoodsReceiptActionMutation();

  const act = async (id: string, action: string): Promise<void> => {
    setFailure(null);
    try {
      await runAction({ id, action }).unwrap();
    } catch (actionError) {
      setFailure(apiErrorMessage(actionError, `That receipt could not be ${action}ed`));
    }
  };

  return (
    <>
      <PageHeader
        title="Goods received"
        description="Deliveries from suppliers. Posting a receipt is what puts the stock away."
      />

      {failure && (
        <div className="mb-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Receipts', value: String(data.summary.count), hint: 'in this period' },
            {
              label: 'Goods received',
              value: formatMoney(String(data.summary.received), format),
              hint: 'at the cost paid',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Receipt number or delivery note"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Awaiting posting</option>
              <option value="POSTED">Posted</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={PackageCheck}
            title={search || status ? 'Nothing matches' : 'No deliveries yet'}
            description="Receipts are raised from a purchase order when goods arrive."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Received</Th>
                  <Th>Supplier</Th>
                  <Th>Order</Th>
                  <Th>Delivery note</Th>
                  <Th numeric>Value</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((receipt: GoodsReceipt) => (
                  <tr key={receipt.id} className="hover:bg-sunken">
                    <Td className="font-medium">{receipt.number}</Td>
                    <Td className="text-secondary">
                      {formatDateTime(receipt.receivedAt, format)}
                    </Td>
                    <Td className="text-secondary">{receipt.supplier.name}</Td>
                    <Td className="text-secondary">{receipt.order?.number ?? 'No order'}</Td>
                    <Td className="text-secondary">{receipt.supplierDeliveryNote ?? '—'}</Td>
                    <Td numeric>{formatMoney(receipt.totalCost, format)}</Td>
                    <Td>
                      <Badge tone={STATUS_TONE[receipt.status] ?? 'neutral'}>
                        {receipt.status === 'DRAFT' ? 'awaiting posting' : receipt.status.toLowerCase()}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={Printer}
                        onClick={() => setSlipFor(receipt.id)}
                      >
                        Slip
                      </Button>
                      {receipt.status === 'DRAFT' && (
                        <Can permission={P.PURCHASE_RECEIVE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() => void act(receipt.id, 'cancel')}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={actionState.isLoading}
                            onClick={() => void act(receipt.id, 'post')}
                            title="Puts the goods into stock at the cost paid"
                          >
                            Post
                          </Button>
                        </Can>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {slipFor && (
        <PurchaseSlip
          receipt={slipReceipt.data}
          loading={slipReceipt.isLoading}
          onClose={() => setSlipFor(null)}
        />
      )}
    </>
  );
}
